import React, { useState, useEffect, useContext } from 'react';
import api from '../../services/api';
import printerService from '../../services/printerService';
import billingService from '../../services/billingService';
import { AuthContext } from '../../context/AuthContext';
import { formatOrderNotes } from '../../utils/formatNotes';
import ConfiguracionSecuenciaModal from './ConfiguracionSecuenciaModal';


const Ordenes = () => {
    const { user } = useContext(AuthContext);
    const roleName = user?.role_details?.name;
    const isAdmin = roleName === 'SUPER_ADMIN' || roleName === 'ADMIN_FAST_FOOD' || user?.is_superuser;

    const exchangeRate = parseFloat(localStorage.getItem('usdExchangeRate')) || 4000;
    const formatOrderValue = (val, method) => {
        const isCop = method && typeof method === 'string' && method.toLowerCase().includes('cop');
        const numericVal = parseFloat(val) || 0;
        if (isCop) {
            return `COP ${(numericVal * exchangeRate).toLocaleString('es-CO')}`;
        }
        return `$${numericVal.toFixed(2)}`;
    };

    const getPaymentMethodBadge = (method) => {
        if (!method) return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' }}>Sin definir</span>;

        const m = String(method).toLowerCase();
        
        if (m.includes('dividido') || m.includes('mixto')) {
            return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#fef3c7', color: '#b45309', border: '1px solid #fde68a', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>⚖️ Mixto</span>;
        }
        if (m.includes('efectivo_cop')) {
            return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#ffedd5', color: '#c2410c', border: '1px solid #fed7aa', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>💵 Efectivo (COP)</span>;
        }
        if (m.includes('transferencia_cop')) {
            return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#e0f2fe', color: '#0369a1', border: '1px solid #7dd3fc', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>🏦 Transfer (COP)</span>;
        }
        if (m.includes('efectivo') || m.includes('cash')) {
            return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #86efac', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>💵 Efectivo</span>;
        }
        if (m.includes('transferencia') || m.includes('transfer')) {
            return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#dbeafe', color: '#1e40af', border: '1px solid #93c5fd', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>🏦 Transferencia</span>;
        }
        if (m.includes('tarjeta') || m.includes('card')) {
            return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f3e8ff', color: '#6b21a8', border: '1px solid #d8b4fe', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>💳 Tarjeta</span>;
        }

        return <span style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f3f4f6', color: '#4b5563', border: '1px solid #e5e7eb' }}>{method}</span>;
    };

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [updatingStatus, setUpdatingStatus] = useState({});
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isEditingPayment, setIsEditingPayment] = useState(false);
    const [newPaymentMethod, setNewPaymentMethod] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 20;

    // Estado para modal de configuración de secuencia / turno
    const [showSequenceModal, setShowSequenceModal] = useState(false);

    // Estado para la calculadora de órdenes seleccionadas
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);

    const toggleSelectOrder = (orderId) => {
        setSelectedOrderIds(prev =>
            prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]
        );
    };

    const toggleSelectAll = (currentPaginated) => {
        const currentIds = currentPaginated.map(o => o.id);
        const allSelected = currentIds.every(id => selectedOrderIds.includes(id));
        if (allSelected) {
            setSelectedOrderIds(prev => prev.filter(id => !currentIds.includes(id)));
        } else {
            setSelectedOrderIds(prev => Array.from(new Set([...prev, ...currentIds])));
        }
    };

    const clearSelection = () => {
        setSelectedOrderIds([]);
    };



    const fetchOrders = async () => {
        try {
            const response = await api.get('/api/orders/orders/', {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });
            setOrders(response.data.results || response.data || []);
        } catch (err) {
            console.error('Error fetching orders:', err);
            setError('Error al cargar las órdenes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);


    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e.key === 'Escape' && showModal) {
                closeModal();
            }
        };

        if (showModal) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [showModal]);

    const handleChangePaymentMethod = async () => {
        if (!newPaymentMethod) return;
        
        try {
            const response = await api.post(
                `/api/orders/orders/${selectedOrder.order_number}/change_payment_method/`,
                { payment_method: newPaymentMethod },
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );

            // Actualizar la orden seleccionada
            setSelectedOrder(prev => ({ ...prev, ...response.data }));
            
            // Actualizar la orden en la lista principal
            setOrders(prevOrders =>
                prevOrders.map(order =>
                    order.order_number === selectedOrder.order_number
                        ? { ...order, ...response.data }
                        : order
                )
            );
            
            setIsEditingPayment(false);
        } catch (err) {
            console.error('Error changing payment method:', err);
            alert(`Error al cambiar método de pago: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleStatusChange = async (orderNumber, newStatus, event) => {
        event.stopPropagation();
        setUpdatingStatus(prev => ({ ...prev, [orderNumber]: true }));
        try {
            const response = await api.post(
                `/api/orders/orders/${orderNumber}/update_status/`,
                { status: newStatus },
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );

            console.log('Status update successful:', response.data);

            setOrders(prevOrders =>
                prevOrders.map(order =>
                    order.order_number === orderNumber
                        ? { ...order, ...response.data }
                        : order
                )
            );
        } catch (err) {
            console.error('Error updating status:', err);
            console.error('Error details:', err.response?.data);
            alert(`Error al actualizar el estado: ${err.response?.data?.detail || err.message}`);
        } finally {
            setUpdatingStatus(prev => ({ ...prev, [orderNumber]: false }));
        }
    };

    const handleRowClick = async (order) => {
        setShowModal(true);
        setSelectedOrder({ ...order, loading: true });

        try {
            const response = await api.get(
                `/api/orders/orders/${order.order_number}/`,
                { baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE }
            );

            let orderData = response.data;
            orderData.customer_name = orderData.customer_name || order.customer_name;

            // Si tiene comprobante emitido pero aún no está en AUTHORIZED, auto-consultar a KoaDoc
            if (orderData.sri_id && orderData.sri_status !== 'AUTHORIZED') {
                try {
                    const statusRes = await billingService.getOrderInvoiceStatus(orderData.id);
                    if (statusRes.success && statusRes.data) {
                        orderData = {
                            ...orderData,
                            sri_status: statusRes.data.status || orderData.sri_status,
                            sri_pdf_url: statusRes.data.pdf_file_url || orderData.sri_pdf_url,
                            sri_xml_url: statusRes.data.xml_file_url || orderData.sri_xml_url,
                            sri_document_number: statusRes.data.document_number || orderData.sri_document_number,
                            sri_access_key: statusRes.data.access_key || orderData.sri_access_key
                        };
                        // Actualizar en el estado de la lista también
                        setOrders(prev => prev.map(o => o.id === orderData.id ? { ...o, ...orderData } : o));
                    }
                } catch (e) {
                    console.warn('No se pudo refrescar estado SRI en vivo:', e);
                }
            }

            setSelectedOrder(orderData);
        } catch (err) {
            console.error('Error fetching order details:', err);
            alert('Error al cargar los detalles de la orden');
            setShowModal(false);
            setSelectedOrder(null);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedOrder(null);
    };

    const handleCancelOrder = async () => {
        if (!selectedOrder) return;

        const reason = window.prompt(`¿Está seguro de anular la Orden ${selectedOrder.order_number}?\nPor favor, ingrese el motivo de la anulación:`);
        
        if (reason === null) {
            return; // El usuario canceló el prompt
        }
        
        if (reason.trim() === '') {
            alert('Debe ingresar un motivo para anular la orden.');
            return;
        }

        try {
            const response = await api.post(`/api/orders/orders/${selectedOrder.order_number}/cancel/`, {
                reason: reason
            }, {
                baseURL: process.env.REACT_APP_FAST_FOOD_SERVICE
            });

            // Actualizar del estado local en vez de eliminarlo
            setOrders(prev => prev.map(o => 
                o.id === selectedOrder.id ? { ...o, ...response.data } : o
            ));
            alert('Orden anulada exitosamente');
            closeModal();
        } catch (err) {
            console.error('Error cancelling order:', err);
            alert(`Error al anular la orden: ${err.response?.data?.detail || err.message}`);
        }
    };

    const handlePrintTicket = async () => {
        if (!selectedOrder) return;

        try {
            const receiptData = {
                order_number: selectedOrder.order_number,
                order_sequence: selectedOrder.order_sequence,
                customer_name: selectedOrder.customer_name || 'CONSUMIDOR FINAL',
                table_number: selectedOrder.table_number || (selectedOrder.order_type === 'takeout' ? 'PARA LLEVAR' : 'MESA GENÉRICA'),
                items: selectedOrder.items.map(item => ({
                    name: item.product_details?.name || item.product_name || 'Producto',
                    quantity: item.quantity,
                    price: parseFloat(item.unit_price),
                    total: parseFloat(item.line_total || item.subtotal),
                    note: item.notes || ''
                })),
                subtotal: parseFloat(selectedOrder.subtotal),
                discount: parseFloat(selectedOrder.discount_amount || 0),
                tax: parseFloat(selectedOrder.tax_amount || 0),
                total: parseFloat(selectedOrder.total),
                payment_method: selectedOrder.payment_method,
                payment_reference: selectedOrder.payment_reference,
                sri_access_key: selectedOrder.sri_access_key,
                sri_document_number: selectedOrder.sri_document_number
            };

            await printerService.printReceipt(receiptData);
            alert('Ticket enviado a la impresora');
        } catch (error) {
            console.error('Error printing ticket:', error);
            alert('Error al imprimir el ticket. Verifique la conexión con el agente de impresión.');
        }
    };

    const handleOpenSequenceModal = () => {
        setShowSequenceModal(true);
    };




    const getStatusDisplay = (status) => {
        if (!status) return '';
        const statusMap = {
            'pending': 'Pendiente',
            'completed': 'Completado',
            'cancelled': 'Anulada'
        };
        return statusMap[status] || status;
    };

    const getStatusColor = (status) => {
        if (!status) return 'bg-gray-100 text-gray-700 border-gray-200';
        const statusColors = {
            'completado': 'bg-green-100 text-green-700 border-green-200',
            'completed': 'bg-green-100 text-green-700 border-green-200',
            'pendiente': 'bg-yellow-100 text-yellow-700 border-yellow-200',
            'pending': 'bg-yellow-100 text-yellow-700 border-yellow-200',
            'anulada': 'bg-red-100 text-red-700 border-red-200',
            'cancelled': 'bg-red-100 text-red-700 border-red-200'
        };
        return statusColors[status.toLowerCase()] || 'bg-gray-100 text-gray-700 border-gray-200';
    };

    const getStatusKey = (statusDisplay) => {
        if (!statusDisplay) return '';
        const reverseMap = {
            'Pendiente': 'pending',
            'Completado': 'completed',
            'Anulada': 'cancelled'
        };
        return reverseMap[statusDisplay] || statusDisplay.toLowerCase();
    };

    const sortedAndFilteredOrders = orders
        .filter(order =>
            order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (order.customer_name && order.customer_name.toLowerCase().includes(searchTerm.toLowerCase()))
        )
        .sort((a, b) => {
            const aCompleted = ['completado', 'completed'].includes(a.status_display?.toLowerCase()) ||
                ['completed'].includes(a.status?.toLowerCase());
            const bCompleted = ['completado', 'completed'].includes(b.status_display?.toLowerCase()) ||
                ['completed'].includes(b.status?.toLowerCase());
            const aCancelled = ['anulada', 'cancelled'].includes(a.status_display?.toLowerCase()) ||
                ['cancelled'].includes(a.status?.toLowerCase());
            const bCancelled = ['anulada', 'cancelled'].includes(b.status_display?.toLowerCase()) ||
                ['cancelled'].includes(b.status?.toLowerCase());
                
            const aInactive = aCompleted || aCancelled;
            const bInactive = bCompleted || bCancelled;

            if (aInactive !== bInactive) {
                return aInactive ? 1 : -1;
            }

            if (!aInactive) {
                return new Date(a.created_at) - new Date(b.created_at);
            } else {
                return new Date(b.created_at) - new Date(a.created_at);
            }
        });

    const totalPages = Math.ceil(sortedAndFilteredOrders.length / itemsPerPage);
    const paginatedOrders = sortedAndFilteredOrders.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const selectedOrdersList = orders.filter(o => selectedOrderIds.includes(o.id));
    const selectedCount = selectedOrdersList.length;
    const selectedTotalUSD = selectedOrdersList.reduce((acc, o) => acc + (parseFloat(o.total) || 0), 0);

    const selectedCashUSD = selectedOrdersList.reduce((acc, o) => {
        const method = String(o.payment_method).toLowerCase();
        if (method.includes('efectivo') || method.includes('cash')) {
            return acc + (parseFloat(o.total) || 0);
        } else if (method.includes('dividido') || method.includes('mixto')) {
            return acc + (parseFloat(o.cash_amount) || 0);
        }
        return acc;
    }, 0);

    const selectedTransferUSD = selectedOrdersList.reduce((acc, o) => {
        const method = String(o.payment_method).toLowerCase();
        if (method.includes('transferencia') || method.includes('transfer')) {
            return acc + (parseFloat(o.total) || 0);
        } else if (method.includes('dividido') || method.includes('mixto')) {
            return acc + (parseFloat(o.transfer_amount) || 0);
        }
        return acc;
    }, 0);

    const selectedCardUSD = selectedOrdersList.reduce((acc, o) => {
        const method = String(o.payment_method).toLowerCase();
        if (method.includes('tarjeta') || method.includes('card')) {
            return acc + (parseFloat(o.total) || 0);
        }
        return acc;
    }, 0);

    const isAllSelected = paginatedOrders.length > 0 && paginatedOrders.every(o => selectedOrderIds.includes(o.id));

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        display: 'inline-block',
                        width: '48px',
                        height: '48px',
                        border: '4px solid #e5e7eb',
                        borderTopColor: '#2e7d32',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{ marginTop: '16px', color: '#4b5563', fontWeight: '500' }}>Cargando órdenes...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', backgroundColor: '#f8fafc' }}>
                <div style={{ backgroundColor: '#fef2f2', borderLeft: '4px solid #ef4444', padding: '24px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', maxWidth: '448px' }}>
                    <p style={{ color: '#991b1b', fontWeight: '500' }}>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', padding: '24px' }}>
            <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <h1 style={{ fontSize: '30px', fontWeight: 'bold', color: '#1f2937', marginBottom: '8px' }}>Órdenes</h1>
                        <p style={{ color: '#6b7280' }}>Gestiona y visualiza todas las órdenes del restaurante</p>
                    </div>
                    <button
                        onClick={handleOpenSequenceModal}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '10px 18px',
                            backgroundColor: '#166534',
                            color: '#ffffff',
                            border: 'none',
                            borderRadius: '8px',
                            fontWeight: '600',
                            fontSize: '14px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(22, 101, 52, 0.2)',
                            transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#14532d'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#166534'}
                    >
                        <span>🔢</span> Configurar Turno / N° Orden
                    </button>
                </div>

                {/* Calculadora de Órdenes Seleccionadas */}
                {selectedCount > 0 && (
                    <div style={{
                        backgroundColor: '#f0fdf4',
                        border: '2px solid #2e7d32',
                        borderRadius: '12px',
                        padding: '16px 24px',
                        marginBottom: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justify: 'space-between',
                        flexWrap: 'wrap',
                        gap: '16px',
                        boxShadow: '0 4px 6px -1px rgba(46, 125, 50, 0.15)'
                    }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <span style={{ fontSize: '20px' }}>🧮</span>
                                <span style={{ fontWeight: '700', fontSize: '16px', color: '#166534' }}>
                                    Sumador de Órdenes Seleccionadas ({selectedCount} órdenes)
                                </span>
                            </div>
                            <div style={{ display: 'flex', gap: '20px', fontSize: '14px', flexWrap: 'wrap', marginTop: '6px' }}>
                                <span style={{ color: '#15803d', fontWeight: '500' }}>
                                    💵 Efectivo: <strong>${selectedCashUSD.toFixed(2)}</strong>
                                </span>
                                <span style={{ color: '#1e40af', fontWeight: '500' }}>
                                    🏦 Transferencia: <strong>${selectedTransferUSD.toFixed(2)}</strong>
                                </span>
                                <span style={{ color: '#6b21a8', fontWeight: '500' }}>
                                    💳 Tarjeta: <strong>${selectedCardUSD.toFixed(2)}</strong>
                                </span>
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                            <div style={{ textAlign: 'right' }}>
                                <div style={{ fontSize: '11px', color: '#166534', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Sumado</div>
                                <div style={{ fontSize: '28px', fontWeight: '800', color: '#2e7d32' }}>
                                    ${selectedTotalUSD.toFixed(2)}
                                </div>
                            </div>
                            <button
                                onClick={clearSelection}
                                style={{
                                    padding: '8px 16px',
                                    borderRadius: '8px',
                                    backgroundColor: '#ffffff',
                                    border: '1.5px solid #cbd5e1',
                                    color: '#475569',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    fontSize: '13px',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f1f5f9'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#ffffff'}
                            >
                                Limpiar Selección
                            </button>
                        </div>
                    </div>
                )}

                {/* Search Bar */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', padding: '16px', marginBottom: '24px' }}>
                    <input
                        type="text"
                        placeholder="Buscar por número de orden o cliente..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '10px 16px',
                            border: '1px solid #d1d5db',
                            borderRadius: '8px',
                            outline: 'none',
                            fontSize: '14px',
                            transition: 'all 0.2s'
                        }}
                        onFocus={(e) => {
                            e.target.style.borderColor = '#2e7d32';
                            e.target.style.boxShadow = '0 0 0 3px rgba(46, 125, 50, 0.1)';
                        }}
                        onBlur={(e) => {
                            e.target.style.borderColor = '#d1d5db';
                            e.target.style.boxShadow = 'none';
                        }}
                    />
                </div>

                {/* Orders Table */}
                <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '2px solid #2e7d32', overflow: 'hidden' }}>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: '#2e7d32', borderBottom: '1px solid #e5e7eb' }}>
                                <tr>
                                    <th style={{ padding: '16px 16px 16px 24px', width: '40px' }}>
                                        <input
                                            type="checkbox"
                                            checked={isAllSelected}
                                            onChange={() => toggleSelectAll(paginatedOrders)}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#2e7d32' }}
                                            title="Seleccionar todas las visibles"
                                        />
                                    </th>
                                    <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        N° Orden
                                    </th>
                                    <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Cliente
                                    </th>
                                    <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Tipo
                                    </th>
                                    <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Total
                                    </th>
                                    <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Método de Pago
                                    </th>
                                    <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Estado
                                    </th>
                                    <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        SRI (Factura)
                                    </th>
                                    <th style={{ padding: '16px 24px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#ffffff', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Fecha
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {paginatedOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" style={{ padding: '48px 24px', textAlign: 'center' }}>
                                            <p style={{ color: '#6b7280', fontWeight: '500', fontSize: '16px' }}>
                                                {searchTerm ? 'No se encontraron órdenes' : 'No hay órdenes registradas'}
                                            </p>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedOrders.map(order => {
                                        const isCompleted = ['completado', 'completed'].includes(order.status_display?.toLowerCase()) ||
                                            ['completed'].includes(order.status?.toLowerCase());
                                        const isCancelled = ['anulada', 'cancelled'].includes(order.status_display?.toLowerCase()) ||
                                            ['cancelled'].includes(order.status?.toLowerCase());
                                        const isSelected = selectedOrderIds.includes(order.id);
                                        return (
                                            <tr
                                                key={order.id}
                                                onClick={() => handleRowClick(order)}
                                                style={{
                                                    borderBottom: '1px solid #e5e7eb',
                                                    transition: 'background-color 0.2s',
                                                    backgroundColor: isSelected ? '#f0fdf4' : 'transparent',
                                                    opacity: (isCompleted || isCancelled) ? 0.7 : 1,
                                                    cursor: 'pointer'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#e8f5e9' : '#f8fafc'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isSelected ? '#f0fdf4' : 'transparent'}
                                            >
                                                <td style={{ padding: '16px 16px 16px 24px', width: '40px' }} onClick={(e) => e.stopPropagation()}>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => toggleSelectOrder(order.id)}
                                                        style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#2e7d32' }}
                                                    />
                                                </td>
                                                <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        {order.order_sequence && (
                                                            <span style={{
                                                                padding: '3px 8px',
                                                                borderRadius: '6px',
                                                                fontSize: '13px',
                                                                fontWeight: '800',
                                                                backgroundColor: '#166534',
                                                                color: '#ffffff',
                                                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                            }}>
                                                                #{order.order_sequence}
                                                            </span>
                                                        )}
                                                        <span style={{ fontWeight: '600', color: '#2563eb' }}>
                                                            {order.order_number}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                                                    <span style={{ color: '#374151' }}>
                                                        {order.customer_name || 'Cliente Casual'}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                                                    <span style={{ color: '#4b5563' }}>
                                                        {order.order_type_display}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                                                    <span style={{ fontWeight: '600', color: 'var(--primary-color)' }}>
                                                        {formatOrderValue(order.total, order.payment_method)}
                                                    </span>
                                                </td>
                                                <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                                                    {getPaymentMethodBadge(order.payment_method)}
                                                </td>
                                                <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                                                    <select
                                                        value={getStatusKey(order.status_display || getStatusDisplay(order.status))}
                                                        onChange={(e) => handleStatusChange(order.order_number, e.target.value, e)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        disabled={updatingStatus[order.order_number] || isCancelled}
                                                        className={getStatusColor(order.status_display || order.status)}
                                                        style={{
                                                            padding: '6px 12px',
                                                            borderRadius: '9999px',
                                                            fontSize: '12px',
                                                            fontWeight: '500',
                                                            border: '1px solid',
                                                            cursor: updatingStatus[order.order_number] ? 'wait' : (isCancelled ? 'not-allowed' : 'pointer'),
                                                            outline: 'none'
                                                        }}
                                                    >
                                                        <option value="pending">Pendiente</option>
                                                        <option value="completed">Completado</option>
                                                        {isCancelled && <option value="cancelled">Anulada</option>}
                                                    </select>
                                                </td>
                                                <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                                                    {order.sri_status === 'AUTHORIZED' ? (
                                                        <a
                                                            href={order.sri_pdf_url || '#'}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={`Clave: ${order.sri_access_key || ''}`}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                padding: '4px 10px',
                                                                borderRadius: '12px',
                                                                fontSize: '11px',
                                                                fontWeight: '600',
                                                                backgroundColor: '#dcfce7',
                                                                color: '#15803d',
                                                                border: '1px solid #86efac',
                                                                textDecoration: 'none'
                                                            }}
                                                        >
                                                            <i className="bi bi-file-earmark-pdf-fill"></i>
                                                            {order.sri_document_number || 'Autorizado'}
                                                        </a>
                                                    ) : ['QUEUED', 'SIGNED', 'SENT', 'PROCESSING'].includes(order.sri_status) ? (
                                                        <span style={{
                                                            padding: '4px 10px',
                                                            borderRadius: '12px',
                                                            fontSize: '11px',
                                                            fontWeight: '600',
                                                            backgroundColor: '#e0f2fe',
                                                            color: '#0369a1',
                                                            border: '1px solid #7dd3fc',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: '4px'
                                                        }}>
                                                            <i className="bi bi-clock-history"></i> {order.sri_status === 'SIGNED' ? 'Firmado' : order.sri_status === 'SENT' ? 'Enviado SRI' : 'En Cola'}
                                                        </span>
                                                    ) : order.sri_status === 'REJECTED' || order.sri_status === 'ERROR' ? (
                                                        <span
                                                            title={order.sri_error_message || 'Rechazado por SRI'}
                                                            style={{
                                                                padding: '4px 10px',
                                                                borderRadius: '12px',
                                                                fontSize: '11px',
                                                                fontWeight: '600',
                                                                backgroundColor: '#fee2e2',
                                                                color: '#b91c1c',
                                                                border: '1px solid #fca5a5',
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                gap: '4px',
                                                                cursor: 'help'
                                                            }}
                                                        >
                                                            <i className="bi bi-exclamation-octagon-fill"></i> Error SRI
                                                        </span>
                                                    ) : order.sri_id ? (
                                                        <span style={{
                                                            padding: '4px 10px',
                                                            borderRadius: '12px',
                                                            fontSize: '11px',
                                                            fontWeight: '600',
                                                            backgroundColor: '#fef3c7',
                                                            color: '#92400e',
                                                            border: '1px solid #fde68a'
                                                        }}>
                                                            {order.sri_document_number || 'Emitido'}
                                                        </span>
                                                    ) : (
                                                        <span style={{
                                                            padding: '4px 10px',
                                                            borderRadius: '12px',
                                                            fontSize: '11px',
                                                            fontWeight: '500',
                                                            backgroundColor: '#f3f4f6',
                                                            color: '#6b7280',
                                                            border: '1px solid #e5e7eb'
                                                        }}>
                                                            Nota Entrega / Sin emitir
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={{ padding: '16px 24px', whiteSpace: 'nowrap' }}>
                                                    <span style={{ fontSize: '14px', color: '#4b5563' }}>

                                                        {new Date(order.created_at).toLocaleString('es-ES', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                    
                    {totalPages > 1 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderTop: '1px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                            <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                                Mostrando del {(currentPage - 1) * itemsPerPage + 1} al {Math.min(currentPage * itemsPerPage, sortedAndFilteredOrders.length)} de {sortedAndFilteredOrders.length} órdenes
                            </p>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    disabled={currentPage === 1}
                                    style={{
                                        padding: '8px 16px',
                                        fontSize: '14px',
                                        borderRadius: '6px',
                                        border: '1px solid #d1d5db',
                                        backgroundColor: currentPage === 1 ? '#f3f4f6' : 'white',
                                        color: currentPage === 1 ? '#9ca3af' : '#374151',
                                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                        fontWeight: '500'
                                    }}
                                >
                                    Anterior
                                </button>
                                <span style={{ padding: '8px 16px', fontSize: '14px', color: '#4b5563', fontWeight: '500', backgroundColor: 'white', border: '1px solid #d1d5db', borderRadius: '6px' }}>
                                    {currentPage} / {totalPages}
                                </span>
                                <button
                                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    disabled={currentPage === totalPages}
                                    style={{
                                        padding: '8px 16px',
                                        fontSize: '14px',
                                        borderRadius: '6px',
                                        border: '1px solid #d1d5db',
                                        backgroundColor: currentPage === totalPages ? '#f3f4f6' : 'white',
                                        color: currentPage === totalPages ? '#9ca3af' : '#374151',
                                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                        fontWeight: '500'
                                    }}
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Stats */}
                {sortedAndFilteredOrders.length > 0 && (
                    <div style={{ marginTop: '24px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', padding: '16px' }}>
                        <p style={{ fontSize: '14px', color: '#4b5563' }}>
                            Mostrando <span style={{ fontWeight: '600', color: '#1f2937' }}>{sortedAndFilteredOrders.length}</span> de{' '}
                            <span style={{ fontWeight: '600', color: '#1f2937' }}>{orders.length}</span> órdenes
                        </p>
                    </div>
                )}
            </div>

            {/* Order Details Modal */}
            {showModal && selectedOrder && (
                <div
                    onClick={closeModal}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000,
                        padding: '20px'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            maxWidth: '600px',
                            width: '100%',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                        }}
                    >
                        {/* Modal Header */}
                        <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        {selectedOrder.order_sequence && (
                                            <span style={{
                                                padding: '4px 10px',
                                                borderRadius: '8px',
                                                fontSize: '16px',
                                                fontWeight: '800',
                                                backgroundColor: '#166534',
                                                color: '#ffffff'
                                            }}>
                                                ORDEN #{selectedOrder.order_sequence}
                                            </span>
                                        )}
                                        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', margin: 0 }}>
                                            {selectedOrder.order_number}
                                        </h2>
                                    </div>
                                    <p style={{ color: '#6b7280', fontSize: '14px', marginTop: '4px', marginBottom: 0 }}>
                                        {new Date(selectedOrder.created_at).toLocaleString('es-ES', {
                                            day: '2-digit',
                                            month: '2-digit',
                                            year: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        })}
                                    </p>
                                </div>
                                <button
                                    onClick={closeModal}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '24px',
                                        cursor: 'pointer',
                                        color: '#6b7280',
                                        padding: '4px 8px',
                                        lineHeight: 1
                                    }}
                                    aria-label="Cerrar modal"
                                >
                                    ×
                                </button>
                                
                                <button
                                    onClick={handlePrintTicket}
                                    style={{
                                        background: 'var(--primary-color)',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 16px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500',
                                        marginLeft: '8px'
                                    }}
                                    aria-label="Imprimir orden"
                                >
                                    Imprimir
                                </button>
                            </div>

                            {/* Cliente destacado en el header */}
                            <div style={{
                                backgroundColor: '#eff6ff',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                border: '1px solid #bfdbfe',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}>
                                <svg
                                    style={{ width: '20px', height: '20px', color: '#2563eb' }}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                <div>
                                    <p style={{ margin: 0, fontSize: '12px', color: '#1e40af', fontWeight: '500' }}>Cliente</p>
                                    <p style={{ margin: 0, fontSize: '16px', color: '#1e3a8a', fontWeight: '600' }}>
                                        {selectedOrder.customer_name || 'Cliente Casual'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Modal Body */}
                        <div style={{ padding: '24px' }}>
                            {/* Order Info */}
                            <div style={{ marginBottom: '24px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                                    Información de la Orden
                                </h3>
                                <div style={{ backgroundColor: 'var(--sidebar-bg)', padding: '16px', borderRadius: '8px' }}>
                                    <p style={{ margin: '0 0 8px 0', color: '#1f2937' }}>
                                        <strong>Tipo de Orden:</strong> {selectedOrder.order_type_display}
                                    </p>
                                    <p style={{ margin: '0 0 8px 0', color: '#1f2937' }}>
                                        <strong>Estado:</strong>{' '}
                                        <span style={{
                                            padding: '4px 10px',
                                            borderRadius: '9999px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            backgroundColor: (selectedOrder.status_display?.toLowerCase() === 'anulada' || selectedOrder.status?.toLowerCase() === 'cancelled') ? '#fee2e2' :
                                                             (selectedOrder.status_display?.toLowerCase() === 'completado' || selectedOrder.status?.toLowerCase() === 'completed') ? '#dcfce7' : '#fef3c7',
                                            color: (selectedOrder.status_display?.toLowerCase() === 'anulada' || selectedOrder.status?.toLowerCase() === 'cancelled') ? '#991b1b' :
                                                   (selectedOrder.status_display?.toLowerCase() === 'completado' || selectedOrder.status?.toLowerCase() === 'completed') ? '#166534' : '#92400e'
                                        }}>
                                            {selectedOrder.status_display || getStatusDisplay(selectedOrder.status)}
                                        </span>
                                    </p>
                                    {selectedOrder.table_number && (
                                        <p style={{ margin: '0', color: '#1f2937' }}>
                                            <strong>Mesa:</strong> {selectedOrder.table_number}
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Order Items */}
                            <div style={{ marginBottom: '24px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                                    Productos
                                </h3>
                                <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
                                    {selectedOrder.loading ? (
                                        <div style={{ padding: '32px', textAlign: 'center' }}>
                                            <div style={{
                                                display: 'inline-block',
                                                width: '32px',
                                                height: '32px',
                                                border: '3px solid #e5e7eb',
                                                borderTopColor: '#2563eb',
                                                borderRadius: '50%',
                                                animation: 'spin 1s linear infinite'
                                            }}></div>
                                            <p style={{ marginTop: '12px', color: '#6b7280' }}>Cargando productos...</p>
                                        </div>
                                    ) : (
                                        // MODO VISUALIZACIÓN: LISTA NORMAL (Código Original)
                                        selectedOrder.items && selectedOrder.items.length > 0 ? (
                                            selectedOrder.items.map((item, index) => {
                                                const hasDiscount = parseFloat(item.discount_percentage) > 0;
                                                const originalTotal = parseFloat(item.unit_price) * item.quantity;
                                                return (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            padding: '12px 16px',
                                                            borderBottom: index < selectedOrder.items.length - 1 ? '1px solid #e5e7eb' : 'none',
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center'
                                                        }}
                                                    >
                                                        <div>
                                                            <p style={{ margin: 0, fontWeight: '500', color: '#1f2937' }}>
                                                                {item.product_details?.name || item.product_name || 'Producto'}
                                                            </p>
                                                            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                                                                Cantidad: {item.quantity} × {formatOrderValue(item.unit_price, selectedOrder.payment_method)}
                                                                {hasDiscount && (
                                                                    <span style={{ marginLeft: '8px', color: '#10b981', fontWeight: '500' }}>
                                                                        (Desc: {parseFloat(item.discount_percentage)}%)
                                                                    </span>
                                                                )}
                                                            </p>
                                                            {item.notes && (
                                                                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#d97706', fontStyle: 'italic', backgroundColor: '#fffbeb', padding: '2px 6px', borderRadius: '4px', display: 'inline-block' }}>
                                                                    Nota: {item.notes}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div style={{ textAlign: 'right' }}>
                                                            {hasDiscount && (
                                                                <p style={{ margin: '0 0 2px 0', fontSize: '12px', textDecoration: 'line-through', color: '#9ca3af' }}>
                                                                    {formatOrderValue(originalTotal, selectedOrder.payment_method)}
                                                                </p>
                                                            )}
                                                            <p style={{ margin: 0, fontWeight: '600', color: hasDiscount ? '#10b981' : 'var(--primary-color)' }}>
                                                                {formatOrderValue(item.line_total || item.subtotal, selectedOrder.payment_method)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <p style={{ padding: '16px', margin: 0, color: '#6b7280', textAlign: 'center' }}>
                                                No hay productos en esta orden
                                            </p>
                                        )
                                    )}
                                </div>
                            </div>

                            {/* Order Summary */}
                            <div style={{ backgroundColor: 'var(--sidebar-bg)', padding: '16px', borderRadius: '8px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <span style={{ color: '#6b7280' }}>Subtotal:</span>
                                    <span style={{ fontWeight: '500', color: '#1f2937' }}>{formatOrderValue(selectedOrder.subtotal, selectedOrder.payment_method)}</span>
                                </div>
                                {selectedOrder.tax_amount > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#6b7280' }}>Impuestos:</span>
                                        <span style={{ fontWeight: '500', color: '#1f2937' }}>{formatOrderValue(selectedOrder.tax_amount, selectedOrder.payment_method)}</span>
                                    </div>
                                )}
                                {selectedOrder.discount_amount > 0 && (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ color: '#6b7280' }}>Descuento:</span>
                                        <span style={{ fontWeight: '500', color: '#ef4444' }}>-{formatOrderValue(selectedOrder.discount_amount, selectedOrder.payment_method)}</span>
                                    </div>
                                )}
                                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '8px', display: 'flex', justifyContent: 'space-between' }}>
                                    <span style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937' }}>Total:</span>
                                    <span style={{ fontSize: '18px', fontWeight: '700', color: 'var(--primary-color)' }}>{formatOrderValue(selectedOrder.total, selectedOrder.payment_method)}</span>
                                </div>
                            </div>

                            {/* Información de Pago */}
                            <div style={{ marginTop: '24px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    Información de Pago
                                    {!isEditingPayment && (
                                        <button 
                                            onClick={() => {
                                                setNewPaymentMethod(selectedOrder.payment_method || 'efectivo');
                                                setIsEditingPayment(true);
                                            }} 
                                            style={{ background: 'none', border: 'none', color: 'var(--primary-color)', cursor: 'pointer', fontSize: '14px' }}
                                        >
                                            <i className="fas fa-edit"></i> Editar
                                        </button>
                                    )}
                                </h3>
                                <div style={{ backgroundColor: '#f3f4f6', padding: '16px', borderRadius: '8px' }}>
                                    {isEditingPayment ? (
                                        <div>
                                            <select 
                                                value={newPaymentMethod} 
                                                onChange={(e) => setNewPaymentMethod(e.target.value)}
                                                style={{ width: '100%', padding: '8px', marginBottom: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                                            >
                                                <option value="efectivo">Efectivo</option>
                                                <option value="transferencia">Transferencia</option>
                                                <option value="tarjeta">Tarjeta</option>
                                            </select>
                                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                <button onClick={() => setIsEditingPayment(false)} style={{ padding: '6px 12px', borderRadius: '4px', border: '1px solid #d1d5db', background: 'white', cursor: 'pointer' }}>Cancelar</button>
                                                <button onClick={handleChangePaymentMethod} style={{ padding: '6px 12px', borderRadius: '4px', border: 'none', background: 'var(--primary-color)', color: 'white', cursor: 'pointer' }}>Guardar</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <p style={{ margin: '0 0 8px 0', color: '#1f2937' }}>
                                                <strong>Método de Pago:</strong> <span style={{ textTransform: 'capitalize' }}>{selectedOrder.payment_method || 'Efectivo'}</span>
                                            </p>
                                            {selectedOrder.payment_method === 'transferencia' && selectedOrder.payment_reference && (
                                                <p style={{ margin: '0', color: '#1f2937' }}>
                                                    <strong>Referencia:</strong> {selectedOrder.payment_reference}
                                                </p>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Notes */}


                            {/* SECCIÓN FACTURACIÓN ELECTRÓNICA SRI (KoaDoc) */}
                            <div style={{ marginTop: '24px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <span><i className="bi bi-file-earmark-check" style={{ color: 'var(--primary-color)' }}></i> Facturación Electrónica SRI</span>
                                    {selectedOrder.sri_status === 'AUTHORIZED' ? (
                                        <span style={{ fontSize: '12px', color: '#15803d', backgroundColor: '#dcfce7', padding: '3px 8px', borderRadius: '10px' }}>
                                            ✓ Autorizado SRI
                                        </span>
                                    ) : selectedOrder.sri_status === 'QUEUED' ? (
                                        <span style={{ fontSize: '12px', color: '#0369a1', backgroundColor: '#e0f2fe', padding: '3px 8px', borderRadius: '10px' }}>
                                            En Cola SRI
                                        </span>
                                    ) : (
                                        <span style={{ fontSize: '12px', color: '#6b7280', backgroundColor: '#f3f4f6', padding: '3px 8px', borderRadius: '10px' }}>
                                            {selectedOrder.sri_status || 'No Emitida'}
                                        </span>
                                    )}
                                </h3>
                                <div style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', padding: '16px', borderRadius: '8px' }}>
                                    {selectedOrder.sri_document_number && (
                                        <p style={{ margin: '0 0 6px 0', fontSize: '14px', color: '#1f2937' }}>
                                            <strong>N° Comprobante:</strong> {selectedOrder.sri_document_number}
                                        </p>
                                    )}
                                    {selectedOrder.sri_access_key && (
                                        <p style={{ margin: '0 0 6px 0', fontSize: '12px', color: '#475569', wordBreak: 'break-all' }}>
                                            <strong>Clave de Acceso:</strong> <code>{selectedOrder.sri_access_key}</code>
                                        </p>
                                    )}
                                    {selectedOrder.sri_error_message && (
                                        <p style={{ margin: '0 0 8px 0', fontSize: '13px', color: selectedOrder.sri_status === 'AUTHORIZED' ? '#15803d' : '#b91c1c' }}>
                                            <strong>Mensaje:</strong> {selectedOrder.sri_error_message}
                                        </p>
                                    )}

                                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px', flexWrap: 'wrap' }}>
                                        {selectedOrder.sri_pdf_url && (
                                            <a
                                                href={selectedOrder.sri_pdf_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#dcfce7',
                                                    color: '#15803d',
                                                    border: '1px solid #86efac',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    textDecoration: 'none',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <i className="bi bi-file-earmark-pdf-fill"></i> Descargar RIDE (PDF)
                                            </a>
                                        )}
                                        {selectedOrder.sri_xml_url && (
                                            <a
                                                href={selectedOrder.sri_xml_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#f1f5f9',
                                                    color: '#475569',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    textDecoration: 'none',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <i className="bi bi-code-square"></i> Descargar XML
                                            </a>
                                        )}
                                        {selectedOrder.sri_id && (
                                            <button
                                                type="button"
                                                onClick={async () => {
                                                    try {
                                                        const res = await billingService.getOrderInvoiceStatus(selectedOrder.id);
                                                        if (res.success) {
                                                            alert(`Estado actual SRI: ${res.data?.status || 'Actualizado'}`);
                                                            fetchOrders();
                                                            closeModal();
                                                        } else {
                                                            alert(res.message || 'No se pudo actualizar el estado.');
                                                        }
                                                    } catch (err) {
                                                        alert('Error al consultar estado: ' + (err.response?.data?.message || err.message));
                                                    }
                                                }}
                                                style={{
                                                    padding: '6px 12px',
                                                    borderRadius: '6px',
                                                    backgroundColor: '#f1f5f9',
                                                    color: '#334155',
                                                    border: '1px solid #cbd5e1',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <i className="bi bi-arrow-clockwise"></i> Consultar Estado SRI
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                try {
                                                    const res = await billingService.emitOrderInvoice(selectedOrder.id);
                                                    alert(res.message || 'Factura emitida con éxito.');
                                                    fetchOrders();
                                                    closeModal();
                                                } catch (err) {
                                                    alert('Error al emitir factura: ' + (err.response?.data?.message || err.message));
                                                }
                                            }}
                                            style={{
                                                padding: '6px 12px',
                                                borderRadius: '6px',
                                                backgroundColor: 'var(--primary-color)',
                                                color: 'white',
                                                border: 'none',
                                                fontSize: '13px',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                        >
                                            <i className="bi bi-send-fill"></i> {selectedOrder.sri_status === 'AUTHORIZED' ? 'Reenviar Factura' : 'Emitir al SRI'}
                                        </button>
                                    </div>
                                </div>
                            </div>


                            {/* SECCIÓN DE EDICIÓN DE NOTAS */}
                            {selectedOrder.notes && (
                                <div style={{ marginTop: '24px' }}>
                                    <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', marginBottom: '12px' }}>
                                        Notas
                                    </h3>
                                    <p style={{ backgroundColor: '#fef3c7', padding: '12px', borderRadius: '8px', margin: 0, color: '#92400e' }}>
                                        {formatOrderNotes(selectedOrder.notes)}
                                    </p>
                                </div>
                            )}


                            {/* BOTONES DE ACCIÓN (FOOTER) */}
                            <div style={{ marginTop: '32px', borderTop: '1px solid #e5e7eb', paddingTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                                {isAdmin && selectedOrder.status !== 'cancelled' && selectedOrder.status_display?.toLowerCase() !== 'anulada' && (
                                    <button
                                        onClick={handleCancelOrder}
                                        style={{
                                            padding: '10px 20px',
                                            backgroundColor: '#fee2e2',
                                            color: '#b91c1c',
                                            border: '1px solid #fca5a5',
                                            borderRadius: '8px',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            marginRight: 'auto'
                                        }}
                                    >
                                        Anular Orden
                                    </button>
                                )}
                                <button
                                    onClick={closeModal}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: 'var(--sidebar-bg)',
                                        color: '#374151',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal para configurar número de orden secuencial y reinicio diario */}
            <ConfiguracionSecuenciaModal
                isOpen={showSequenceModal}
                onClose={() => setShowSequenceModal(false)}
            />


            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default Ordenes;